import { useMemo } from "react";
import type {
	ClientActivityAgentItemResponseDTO,
	ClientActivityParticipantItemResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";

type ParticipantGroup = {
	role: string;
	names: string;
};

const R = Resources.Activity.Detail;

const joinNames = (
	items: { name?: string | null }[],
	emptyName: string,
): string => items.map((item) => item.name ?? emptyName).join(", ");

export const useActivityParticipantGroups = (
	participants: ClientActivityParticipantItemResponseDTO[],
	agents: ClientActivityAgentItemResponseDTO[],
): ParticipantGroup[] => {
	const { t } = useResource();

	return useMemo(() => {
		const { clients, others } = participants.reduce<{
			clients: ClientActivityParticipantItemResponseDTO[];
			others: ClientActivityParticipantItemResponseDTO[];
		}>(
			(groups, participant) => {
				const group = participant.role === "Client" ? "clients" : "others";
				groups[group].push(participant);
				return groups;
			},
			{ clients: [], others: [] },
		);

		const { regularAgents, assistantAgents } = agents.reduce<{
			regularAgents: ClientActivityAgentItemResponseDTO[];
			assistantAgents: ClientActivityAgentItemResponseDTO[];
		}>(
			(groups, agent) => {
				const group = agent.isAssistant ? "assistantAgents" : "regularAgents";
				groups[group].push(agent);
				return groups;
			},
			{ regularAgents: [], assistantAgents: [] },
		);

		const groups: ParticipantGroup[] = [];

		if (clients.length > 0) {
			groups.push({
				role: t(R.Participants.client),
				names: joinNames(clients, "-"),
			});
		}
		if (regularAgents.length > 0) {
			groups.push({
				role: t(R.Agents.advisor),
				names: joinNames(regularAgents, "-"),
			});
		}
		if (assistantAgents.length > 0) {
			groups.push({
				role: t(R.Agents.assistant),
				names: joinNames(assistantAgents, "-"),
			});
		}
		groups.push(
			...others.map((participant) => ({
				role: participant.role ?? t(R.OtherClient),
				names: participant.name ?? "",
			})),
		);

		return groups;
	}, [agents, participants, t]);
};
